import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import ScrollableTableWrapper from "../../../components/ScrollableTableWrapper";
import type { EnrichedHoldingRow } from "../../../hooks/useStrategyHoldings";
import type { StrategyHoldingsSummary } from "../../../utils/strategyFlatPositions";
import { formatCurrencyWithCommas, formatNumber, formatSignedPercentage } from "../../../utils/numberUtils";
import { PIE_COLORS } from "./StrategyDonutCard";

function formatOptionExpiry(iso: string): string {
  if (!iso) return "--";
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return iso;
  const [, y, m, d] = match;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[parseInt(m, 10) - 1]} ${parseInt(d, 10)}, ${y.slice(2)}`;
}

const MAX_INITIAL_ROWS = 20;

interface StrategyHoldingsSectionProps {
  holdings: EnrichedHoldingRow[];
  summary: StrategyHoldingsSummary;
  missingProducts?: string[];
  isLoading: boolean;
  nlv: number | null;
  title?: string;
  productColorMap: Map<string, string>;
  appearance?: "default" | "alpha";
}

function DirectionBadge({
  direction,
  isConflict,
  appearance,
}: {
  direction: string;
  isConflict: boolean;
  appearance: "default" | "alpha";
}) {
  if (isConflict) {
    return (
      <span className={`font-mono text-xs tabular-nums ${appearance === "alpha" ? "text-v7-bronze-dark" : "text-amber-400"}`}>
        NET {direction}
      </span>
    );
  }
  const color = appearance === "alpha"
    ? direction === "LONG" ? "text-v7-success" : direction === "SHORT" ? "text-v7-rust" : "text-v7-ink-soft"
    : direction === "LONG" ? "text-green-500" : direction === "SHORT" ? "text-red-500" : "text-gray-500";
  return <span className={`${color} font-mono text-xs tabular-nums`}>{direction}</span>;
}

function SourceTags({
  contributions,
  productColorMap,
  appearance,
}: {
  contributions: EnrichedHoldingRow["contributions"];
  productColorMap: Map<string, string>;
  appearance: "default" | "alpha";
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {contributions.map((c, i) => {
        const color = productColorMap.get(c.productKey) ?? "#6b7280";
        return (
          <span
            key={i}
            className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] ${appearance === "alpha"
              ? "bg-v7-paper text-v7-ink-soft"
              : "bg-white/[0.06] text-gray-400"}`}
            style={{ borderLeft: `2px solid ${color}` }}
          >
            {c.productName.length > 16 ? c.productName.slice(0, 14) + ".." : c.productName}{" "}
            <span className="font-mono tabular-nums">
              {formatSignedPercentage(c.effectiveWeight, 1)}
            </span>
          </span>
        );
      })}
    </div>
  );
}

function SummaryLine({
  summary,
  productCount,
  appearance,
}: {
  summary: StrategyHoldingsSummary;
  productCount: number;
  appearance: "default" | "alpha";
}) {
  const parts: string[] = [];
  if (summary.longCount > 0) parts.push(`${summary.longCount} Long`);
  if (summary.shortCount > 0) parts.push(`${summary.shortCount} Short`);
  if (summary.optionCount > 0) parts.push(`${summary.optionCount} Options`);
  return (
    <span className={`text-xs ${appearance === "alpha" ? "text-v7-ink/60" : "text-gray-500"}`}>
      {summary.uniquePositions} unique positions across {productCount} products
      {parts.length > 0 && <> &middot; {parts.join(" / ")}</>}
    </span>
  );
}

const StrategyHoldingsSection: React.FC<StrategyHoldingsSectionProps> = ({
  holdings,
  summary,
  missingProducts = [],
  isLoading,
  nlv,
  title = "Underlying Holdings",
  productColorMap,
  appearance = "default",
}) => {
  const isAlpha = appearance === "alpha";
  const [expanded, setExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const productCount = new Set(
    holdings.flatMap((h) => h.contributions.map((c) => c.productKey))
  ).size;

  const hasOptions = holdings.some((h) => h.contractType === "OPTION");
  const visibleRows = showAll ? holdings : holdings.slice(0, MAX_INITIAL_ROWS);
  const hasMore = holdings.length > MAX_INITIAL_ROWS;

  return (
    <div className={`overflow-hidden border ${isAlpha
      ? "rounded-v7-panel border-v7-ink/15 bg-v7-white"
      : "rounded-lg border-gray-200 bg-white dark:border-rp-separator dark:bg-rp-surface"}`}>
      <div
        className={`flex cursor-pointer items-center justify-between px-5 py-3.5 transition-colors ${isAlpha
          ? "hover:bg-v7-paper"
          : "hover:bg-gray-50 dark:hover:bg-white/[0.015]"}`}
        onClick={() => setExpanded((p) => !p)}
      >
        <div className="flex items-center gap-3">
          <span className={`text-sm font-semibold ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>{title}</span>
          {!isLoading && (
            <SummaryLine summary={summary} productCount={productCount} appearance={appearance} />
          )}
          {isLoading && (
            <span className={`text-xs ${isAlpha ? "text-v7-ink/50" : "text-gray-500"}`}>Loading...</span>
          )}
        </div>
        <ChevronDown
          className={`h-4 w-4 transition-transform duration-300 ${isAlpha ? "text-v7-ink/50" : "text-gray-400"} ${expanded ? "rotate-180" : ""}`}
        />
      </div>

      {expanded && (
        <div className={`border-t ${isAlpha ? "border-v7-ink/15" : "border-gray-200 dark:border-rp-separator"}`}>
          {missingProducts.length > 0 && (
            <div className={`border-b px-4 py-2.5 ${isAlpha ? "border-v7-bronze/30 bg-v7-bronze/10" : "border-amber-500/20 bg-amber-500/10"}`}>
              <span className={`text-xs ${isAlpha ? "text-v7-bronze-dark" : "text-amber-400"}`}>
                Incomplete: product data unavailable for {missingProducts.join(", ")}
              </span>
            </div>
          )}
          {isLoading ? (
            <div className="px-5 py-8 text-center">
              <span className={`text-sm ${isAlpha ? "text-v7-ink/50" : "text-gray-500"}`}>Loading holdings...</span>
            </div>
          ) : holdings.length === 0 ? (
            <div className="px-5 py-8 text-center">
              <span className={`text-sm ${isAlpha ? "text-v7-ink/50" : "text-gray-500"}`}>No holdings data available</span>
            </div>
          ) : (
            <>
              <ScrollableTableWrapper>
                <table className="w-full min-w-[750px]">
                  <thead>
                    <tr className={isAlpha ? "border-b border-v7-ink/15 bg-v7-paper" : "border-b border-gray-200 dark:border-rp-separator"}>
                      <th className={`px-4 py-2.5 text-left text-[10px] uppercase tracking-wider ${isAlpha ? "text-v7-ink-soft" : "text-gray-500"}`}>Ticker</th>
                      <th className={`px-4 py-2.5 text-left text-[10px] uppercase tracking-wider ${isAlpha ? "text-v7-ink-soft" : "text-gray-500"}`}>Name</th>
                      {hasOptions && (
                        <>
                          <th className={`py-2.5 pl-2 pr-1 text-left text-[10px] uppercase tracking-wider ${isAlpha ? "text-v7-ink-soft" : "text-gray-500"}`}>Exp.</th>
                          <th className={`px-1 py-2.5 text-right text-[10px] uppercase tracking-wider ${isAlpha ? "text-v7-ink-soft" : "text-gray-500"}`}>Strike</th>
                          <th className={`py-2.5 pl-1 pr-2 text-left text-[10px] uppercase tracking-wider ${isAlpha ? "text-v7-ink-soft" : "text-gray-500"}`}>P/C</th>
                        </>
                      )}
                      <th className={`px-4 py-2.5 text-right text-[10px] uppercase tracking-wider ${isAlpha ? "text-v7-ink-soft" : "text-gray-500"}`}>Direction</th>
                      <th className={`px-4 py-2.5 text-right text-[10px] uppercase tracking-wider ${isAlpha ? "text-v7-ink-soft" : "text-gray-500"}`}>Net Weight</th>
                      {nlv != null && (
                        <th className={`px-4 py-2.5 text-right text-[10px] uppercase tracking-wider ${isAlpha ? "text-v7-ink-soft" : "text-gray-500"}`}>Net $ Target</th>
                      )}
                      <th className={`px-4 py-2.5 text-left text-[10px] uppercase tracking-wider ${isAlpha ? "text-v7-ink-soft" : "text-gray-500"}`}>Sources</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row) => (
                      <tr
                        key={`${row.conId}-${row.direction}`}
                        className={`border-b transition-colors last:border-b-0 ${isAlpha
                          ? "border-v7-ink/10 hover:bg-v7-paper"
                          : "border-gray-100 hover:bg-gray-50 dark:border-rp-separator dark:hover:bg-white/[0.02]"} ${
                          row.isConflict ? "border-l-[3px] border-l-amber-400" : ""
                        }`}
                      >
                        <td className="px-4 py-2 text-left">
                          <span className={`font-mono text-sm font-semibold tabular-nums ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>
                            {row.ticker}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-left">
                          <span className={`block max-w-[180px] truncate text-xs ${isAlpha ? "text-v7-ink-soft" : "text-gray-500 dark:text-gray-400"}`}>
                            {row.name}
                          </span>
                        </td>
                        {hasOptions && (
                          <>
                            <td className={`py-2 pl-2 pr-1 text-left text-xs ${isAlpha ? "text-v7-ink-soft" : "text-gray-500 dark:text-gray-400"}`}>
                              {row.contractType === "OPTION" ? formatOptionExpiry(row.expiration) : ""}
                            </td>
                            <td className={`px-1 py-2 text-right font-mono text-sm tabular-nums ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>
                              {row.contractType === "OPTION" && row.strike > 0 ? formatNumber(row.strike) : ""}
                            </td>
                            <td className={`py-2 pl-1 pr-2 text-left text-xs ${isAlpha ? "text-v7-ink-soft" : "text-gray-500 dark:text-gray-400"}`}>
                              {row.contractType === "OPTION" ? row.right || "--" : ""}
                            </td>
                          </>
                        )}
                        <td className="px-4 py-2 text-right">
                          <DirectionBadge direction={row.direction} isConflict={row.isConflict} appearance={appearance} />
                        </td>
                        <td className={`px-4 py-2 text-right font-mono tabular-nums text-sm ${
                          row.isConflict
                            ? isAlpha ? "text-v7-bronze-dark" : "text-amber-400"
                            : isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"
                        }`}>
                          {formatSignedPercentage(row.netWeight, 1)}
                        </td>
                        {nlv != null && (
                          <td className={`px-4 py-2 text-right font-mono tabular-nums text-sm ${
                            row.isConflict
                              ? isAlpha ? "text-v7-bronze-dark" : "text-amber-400"
                              : isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"
                          }`}>
                            {formatCurrencyWithCommas((row.netWeight / 100) * nlv, 0)}
                          </td>
                        )}
                        <td className="px-4 py-2 text-left">
                          <SourceTags contributions={row.contributions} productColorMap={productColorMap} appearance={appearance} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollableTableWrapper>

              {hasMore && !showAll && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowAll(true); }}
                  className={`w-full border-t py-2.5 text-center text-xs transition-colors ${isAlpha
                    ? "border-v7-ink/10 text-v7-ink/60 hover:bg-v7-paper hover:text-v7-ink"
                    : "border-gray-100 text-gray-500 hover:text-gray-300 dark:border-rp-separator"}`}
                >
                  + {holdings.length - MAX_INITIAL_ROWS} more positions
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export function buildProductColorMap(
  entries: Array<{ entityType: string; productId: number }>
): Map<string, string> {
  const map = new Map<string, string>();
  entries.forEach((e, idx) => {
    map.set(`${e.entityType}-${e.productId}`, PIE_COLORS[idx % PIE_COLORS.length]);
  });
  return map;
}

export default StrategyHoldingsSection;
